import React, {useState, useCallback, useEffect} from 'react';
import produce from 'immer';
import {useTranslation} from 'react-i18next';
import SMCModal from '../../../components/core/SMCModal';
import {setProperty} from '../../../utils/utils';
import {RoleRuleSchema} from '../../../api/DBSchema/Role';
import RoleRuleEditorStepOne from './RoleRuleEditorStepOne';
import RoleRuleEditorStepTwo from './RoleRuleEditorStepTwo';
import {SystemRoleRuleSchema} from '../../../api/DBSchema/SystemRole';

function RoleRuleEditor({
    isOpen,
    onRequestClose,
    onSuccess,
    defaultObject,
    roleType
}) {
    const { t } = useTranslation();

    const isSystemRole = roleType === 'system';

    const [ruleObject, setRuleObject] = useState(defaultObject);
    const [editorStep, setEditorStep] = useState(0);
    
    const setRuleObjectValue = useCallback((paramPath, newValue) => {
        setRuleObject(ruleObject => produce(ruleObject, draft => {
            setProperty(draft, paramPath, newValue);
        }));
    }, []);

    const onFinish = useCallback(() => {
        const type = ruleObject[SystemRoleRuleSchema.type];
        const typeValues = SystemRoleRuleSchema.typeValues;
        const target = ruleObject[SystemRoleRuleSchema.subject][SystemRoleRuleSchema.subjectSchema.target];
        const actions = ruleObject[SystemRoleRuleSchema.actions];
        const actionsValues = SystemRoleRuleSchema.actionsValues;

        // Users can't create or delete system users (only admins can)
        if (
            target === SystemRoleRuleSchema.subjectSchema.targetValues.SYSTEM_USER &&
            type === typeValues.ALLOW &&
            (actions.includes(actionsValues.CREATE) || actions.includes(actionsValues.DELETE))
        ) {
            alert('System User cannot have create/delete permission');
        }
        else {
            onSuccess(ruleObject);
        }
    }, [onSuccess, ruleObject]);

    useEffect(() => {
        if (isOpen) {
            if (defaultObject) {
                setRuleObject(defaultObject);
            } else {
                const defaultRuleObject = {
                    [RoleRuleSchema.type]: RoleRuleSchema.typeValues.ALLOW,
                    [RoleRuleSchema.actions]: [],
                    [RoleRuleSchema.subject]: {
                        [RoleRuleSchema.subjectSchema.target]: RoleRuleSchema.subjectSchema.targetValues.ANY,
                        [RoleRuleSchema.subjectSchema.condition]: ''
                    }
                };
                setRuleObject(defaultRuleObject);
            }
            setEditorStep(0);
        }
    }, [isOpen, defaultObject]);

    let EditorBody = null;

    switch (editorStep) {
        case 0:
            EditorBody = RoleRuleEditorStepOne;
            break;
        case 1:
            EditorBody = RoleRuleEditorStepTwo;
            break;
        default:
            onRequestClose();
            break;
    }

    return (
        <SMCModal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            width={750}
            height={590}
            title={t('SMC_MESSEGE_TITLE')}
        >
            <EditorBody
                onCancel={onRequestClose}
                onNext={() => setEditorStep(step => step+1)}
                onBack={() => setEditorStep(step => step-1)}
                onFinish={onFinish}
                object={ruleObject}
                setObjectValue={setRuleObjectValue}
                isSystemRole={isSystemRole}
            />
        </SMCModal>
    );
}

export default React.memo(RoleRuleEditor);